import {
	APP_INITIALIZER,
	ApplicationRef,
	DoBootstrap,
	Injector,
	NgModule,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import {
	HTTP_INTERCEPTORS,
	HttpClientModule,
} from '@angular/common/http';
import {
	forkJoin,
	from,
	of,
} from 'rxjs';
import { catchError } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { UrlService } from '@uirouter/core';
import * as FontFaceObserver from 'fontfaceobserver';

import { CommonLocaleService } from '../services/locale/locale.service';
import { CommonBootstrapDataService } from '../services/bootstrap/bootstrap_data.service';
import { CommonUserDataService } from '../services/user_data.service';
import {
	CommonAppDataService,
	commonAppDataServiceFactory,
} from '../services/app_data/app_data.service';
import { LOCAL_APP_DATA_GETTER } from '../services/app_data/local_app_data.token';
import { COMMON_APP_CONFIG } from './app.config.token';
import { CommonNotificationService } from '../services/notification.service';
import { CommonGeneralSettingsService } from '../services/general_settings.service';
import { CommonLocaleModule } from '../services/locale/locale.module';
import { CommonErrorInterceptor } from '../interceptors/error_interceptor.service';
import { CommonServerDateService } from '../services/server_date.service';
import { CommonPipesModule } from '../pipes/pipes.module';
import { CommonDirectivesModule } from '../directives/directives.module';
import { CommonBootstrapService } from '../services/bootstrap/bootstrap.service';
import { CommonTextfieldInputMaskAliasesService } from '../controls/textfield/inputmasks_aliases_service';
import { CommonAppLayoutService } from '../services/common_app_layout.service';
import { setAnimateProgress } from '../vendor/common_animate_progress';
import { setRipples } from '../vendor/ripples';
import { isFunction } from 'lodash';

let animate: IAnimateProgressActions;

@NgModule({
	imports: [
		BrowserModule,
		HttpClientModule,
		CommonLocaleModule,
		CommonPipesModule,
		CommonDirectivesModule,
	],
	exports: [
		BrowserModule,
		HttpClientModule,
		TranslateModule,
		CommonLocaleModule,
		CommonPipesModule,
		CommonDirectivesModule,
	],
	providers: [
		CommonBootstrapDataService,
		CommonUserDataService,
		CommonNotificationService,
		{
			provide: CommonAppDataService,
			useFactory: commonAppDataServiceFactory,
			deps: [LOCAL_APP_DATA_GETTER, COMMON_APP_CONFIG],
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: CommonErrorInterceptor,
			multi: true,
		},
		CommonGeneralSettingsService,
		CommonServerDateService,
		CommonBootstrapService,
		{
			provide: APP_INITIALIZER,
			useFactory: appInitializerFactory,
			deps: [
				UrlService,
				CommonLocaleService,
				CommonAppDataService,
				CommonUserDataService,
				CommonBootstrapDataService,
				CommonAppLayoutService,
			],
			multi: true,
		},
	],
})
export class CommonAppModule implements DoBootstrap {
	protected commonAppDataService: CommonAppDataService = this.injector.get(CommonAppDataService);
	protected commonLocaleService: CommonLocaleService = this.injector.get(CommonLocaleService);
	private app: ApplicationRef = this.injector.get(ApplicationRef);
	private commonTextfieldInputMaskAliasesService: CommonTextfieldInputMaskAliasesService = this.injector.get(CommonTextfieldInputMaskAliasesService);
	private commonServerDateService = this.injector.get(CommonServerDateService);
	private commonAppLayoutService = this.injector.get(CommonAppLayoutService);
	private commonNotificationService = this.injector.get(CommonNotificationService);

	private appData = this.commonAppDataService.getData();

	constructor (
		protected injector: Injector,
	) {
		// выполнять какие-либо действия нужно после инициализации приложения (когда уже инициализированы переводы и тд), но подписываться на изменения нужно в конструкторе
		// поэтому организовано через observable
		if (this.appData.shouldInitLayoutService) {
			this.commonAppLayoutService.initWatchTransitionChanges();
		}
	}

	ngDoBootstrap () {
		this.appModuleRun(this.app);
	}

	appModuleRun (
		app: ApplicationRef,
	) {
		/**
		 * Выполняется после APP_INITIALIZER. Все данные в сервисах уже есть.
		 */
		if (this.appData.shouldInitLayoutService) {
			this.commonAppLayoutService.subscribeOtherTransition();
		}

		this.commonServerDateService.init();
		this.commonLocaleService.setFormats();
		this.commonTextfieldInputMaskAliasesService.init();

		if (this.appData.mainComponent) {
			app.bootstrap(this.appData.mainComponent);
		}

		if (isFunction(this.appData.callback)) {
			this.appData.callback();
		}
		const { appStartNotification } = this.commonAppDataService.getData();
		if (appStartNotification) {
			this.commonNotificationService.show(appStartNotification.Header, appStartNotification.Message, appStartNotification.Type);
		}

		disableLoader();
	}
}

export function appInitializerFactory (
	urlService: UrlService,
	commonLocaleService: CommonLocaleService,
	commonAppDataService: CommonAppDataService,
	commonUserDataService: CommonUserDataService,
	commonBootstrapDataService: CommonBootstrapDataService,
	commonAppLayoutService: CommonAppLayoutService,
) {
	const FONT_LOADING_TIMEOUT = 60000;
	const { hasInitialLoader, shouldAuthorize, shouldInitLayoutService } = commonAppDataService.getData();

	// останавливаем UiRouter до полной загрузки данных
	urlService.deferIntercept();

	return () => {
		/**
		 * Выполняется до ngDoBootstrap. ngDoBootstrap ждет загрузки этих данных
		 */
		setAnimateProgress($);
		setRipples($);

		if (hasInitialLoader) {
			enableLoader();
		}

		const promises = [
			commonLocaleService.init(),
			commonUserDataService.init(shouldAuthorize),
			from(new FontFaceObserver('Roboto', { weight: 400 }).load('', FONT_LOADING_TIMEOUT))
				.pipe(catchError(() => of(undefined))),
			from(new FontFaceObserver('Roboto', { weight: 500 }).load('', FONT_LOADING_TIMEOUT))
				.pipe(catchError(() => of(undefined))),
			from(new FontFaceObserver('Roboto', { weight: 700 }).load('', FONT_LOADING_TIMEOUT))
				.pipe(catchError(() => of(undefined))),
		];

		if (!shouldAuthorize) {
			promises.push(commonBootstrapDataService.init(false));
		}

		if (shouldInitLayoutService) {
			commonAppLayoutService.subscribeOnBeforeTransition();
		}

		// !!! внутри toPromise уже вызывается subscribe
		// https://github.com/ReactiveX/rxjs/blob/master/src/internal/Observable.ts#L474
		return forkJoin(promises)
			.toPromise()
			.then(() => {
				// стартуем UiRouter только когда все данные загружены
				// https://bankro.atlassian.net/browse/CTB-2038
				urlService.listen();
				urlService.sync();
			});
	};
}

function enableLoader () {
	animate = $.animateProgress({
		label: 'Загрузка',
		container: '.app-loading',
	});
	animate.enable();
}

function disableLoader () {
	if (animate) {
		animate.disable();
	}

	$('.app-loading').remove();
}
